import { useState, useEffect } from 'react';
import { BookOpen, Copy, RefreshCw, Eye, EyeOff, Save, Trash2, Plus } from 'lucide-react';
import ScheduledQueuePicker from '@/components/ScheduledQueuePicker';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApiService, type CatalogScheduledQueue } from '@/services/catalogApi.service';
import { queueApiService } from '@/services/queueApi.service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
export default function EventsCatalogo() {
  useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showToken, setShowToken] = useState(false);
  const [highlightQueueId, setHighlightQueueId] = useState<string>('');
  const [scheduledQueues, setScheduledQueues] = useState<CatalogScheduledQueue[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [customToken, setCustomToken] = useState('');
  const [newToken, setNewToken] = useState('');
  const [showScheduledPicker, setShowScheduledPicker] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => catalogApiService.getCatalog(),
  });

  // Sync catalog data to local state once on load
  useEffect(() => {
    if (catalog && !initialized) {
      setHighlightQueueId(catalog.highlightQueueId || '');
      setScheduledQueues(catalog.scheduledQueues || []);
      setIsActive(catalog.isActive);
      setInitialized(true);
    } else if (catalog === null && !initialized) {
      setInitialized(true);
    }
  }, [catalog, initialized]);

  const { data: queues, isLoading: queuesLoading } = useQuery({
    queryKey: ['queues'],
    queryFn: () => queueApiService.listQueues(),
  });

  const availableQueues = (queues || []).filter(
    (q: any) => q.realStatus === 'active' || q.realStatus === 'paused'
  );

  const saveMutation = useMutation({
    mutationFn: async (dto: { highlightQueueId?: string; scheduledQueues: CatalogScheduledQueue[]; isActive: boolean }) => {
      if (!catalog) {
        return catalogApiService.createCatalog(dto);
      }
      return catalogApiService.updateCatalog(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      toast({ title: 'Salvo!', description: 'Catálogo atualizado com sucesso.' });
    },
    onError: () => {
      toast({ title: 'Erro', description: 'Não foi possível salvar o catálogo.', variant: 'destructive' });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (customToken?: string) => catalogApiService.regenerateToken(customToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      setShowRegenerateDialog(false);
      setNewToken('');
      toast({ title: 'Token regenerado!', description: 'O novo token está ativo imediatamente.' });
    },
    onError: () => {
      toast({ title: 'Erro', description: 'Não foi possível regenerar o token.', variant: 'destructive' });
    },
  });

  const maskedToken = catalog?.accessToken
    ? showToken
      ? catalog.accessToken
      : `${catalog.accessToken.slice(0, 8)}${'*'.repeat(24)}`
    : '';

  const publicCatalogUrl = catalog?.accessToken
    ? `${window.location.origin}/vitrine/${catalog.accessToken}`
    : '';

  const handleCopyToken = () => {
    navigator.clipboard.writeText(catalog?.accessToken || '');
    toast({ title: 'Copiado!', description: 'Token copiado para a área de transferência.' });
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(publicCatalogUrl);
    toast({ title: 'Copiado!', description: 'URL pública copiada para a área de transferência.' });
  };

  const handleSave = () => {
    saveMutation.mutate({
      highlightQueueId: highlightQueueId || undefined,
      scheduledQueues,
      isActive,
      ...(!catalog && { accessToken: customToken || undefined }),
    });
  };

  const handleToggleActive = (value: boolean) => {
    if (!value) {
      setShowDeactivateDialog(true);
    } else {
      setIsActive(true);
    }
  };

  const handleConfirmDeactivate = () => {
    setIsActive(false);
    setShowDeactivateDialog(false);
  };

  const handleAddScheduledQueue = (queueId: string, scheduledTime: string) => {
    setScheduledQueues((prev) => [...prev, { queueId, scheduledTime }]);
  };

  const handleRemoveScheduledQueue = (queueId: string, scheduledTime: string) => {
    setScheduledQueues((prev) =>
      prev.filter((sq) => !(sq.queueId === queueId && sq.scheduledTime === scheduledTime))
    );
  };

  const isLoading = catalogLoading || queuesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="loading-skeleton">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-3/10">
          <BookOpen className="text-brand-logo-3" size={20} />
        </div>
        <div>
          <h1 className="text-[16px] font-bold text-foreground">Catálogo de Filas</h1>
          <p className="text-[13px] text-muted-foreground">Configure o catálogo público de filas do evento</p>
        </div>
      </div>

      {/* Token de Acesso */}
      <Card>
        <CardHeader>
          <CardTitle>Token de Acesso</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {catalog ? (
            <>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono text-foreground break-all">
                  {maskedToken}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowToken((v) => !v)}
                  title={showToken ? 'Ocultar token' : 'Mostrar token'}
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button variant="outline" size="icon" onClick={handleCopyToken} title="Copiar token">
                  <Copy size={16} />
                </Button>
              </div>

              {publicCatalogUrl && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">URL pública do catálogo:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono text-foreground break-all">
                      {publicCatalogUrl}
                    </code>
                    <Button variant="outline" size="icon" onClick={handleCopyUrl} title="Copiar URL">
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setShowRegenerateDialog(true)}
                className="flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Regenerar Token
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Token de Acesso (opcional)</label>
              <Input
                placeholder="meu-evento-2026 (mínimo 8 caracteres)"
                value={customToken}
                onChange={(e) => setCustomToken(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Deixe em branco para gerar automaticamente</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fila Principal */}
      <Card>
        <CardHeader>
          <CardTitle>Fila Principal (Destaque)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Selecione a fila que será destacada no catálogo público.
            </p>
            <Select
              value={highlightQueueId}
              onValueChange={setHighlightQueueId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Nenhuma fila selecionada" />
              </SelectTrigger>
              <SelectContent>
                {availableQueues.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    Nenhuma fila ativa ou pausada
                  </SelectItem>
                ) : (
                  availableQueues.map((q: any) => (
                    <SelectItem key={q.id} value={q.id}>
                      <span className="flex items-center gap-2">
                        {q.nome}
                        <Badge variant={q.realStatus === 'active' ? 'default' : 'secondary'}>
                          {q.realStatus === 'active' ? 'Ativa' : 'Pausada'}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Filas Agendadas */}
      <Card>
        <CardHeader>
          <CardTitle>Filas Agendadas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {scheduledQueues.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma fila agendada adicionada.</p>
          ) : (
            <ul className="space-y-2">
              {[...scheduledQueues]
                .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime))
                .map((sq, index) => {
                  const queue = (queues || []).find((q: any) => q.id === sq.queueId);
                  return (
                    <li
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted rounded-ds-card"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-foreground">{sq.scheduledTime}</span>
                        <span className="text-foreground">{queue?.nome || sq.queueId}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveScheduledQueue(sq.queueId, sq.scheduledTime)}
                        title="Remover fila agendada"
                      >
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </li>
                  );
                })}
            </ul>
          )}

          <Button
            variant="outline"
            onClick={() => setShowScheduledPicker(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Adicionar Fila Agendada
          </Button>
        </CardContent>
      </Card>

      {/* Toggle de Ativação */}
      <Card>
        <CardHeader>
          <CardTitle>Ativação do Catálogo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Switch
              checked={isActive}
              onCheckedChange={handleToggleActive}
              id="catalog-active"
            />
            <label htmlFor="catalog-active" className="text-foreground cursor-pointer">
              {isActive ? 'Catálogo ativo' : 'Catálogo desativado'}
            </label>
          </div>
          {!isActive && (
            <p className="text-sm text-destructive mt-2">
              O link público do catálogo deixará de funcionar para todos os participantes.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2"
        >
          <Save size={16} />
          {saveMutation.isPending ? 'Salvando...' : 'Salvar Catálogo'}
        </Button>
      </div>

      {/* ScheduledQueuePicker */}
      <ScheduledQueuePicker
        open={showScheduledPicker}
        onOpenChange={setShowScheduledPicker}
        scheduledQueues={scheduledQueues}
        highlightQueueId={highlightQueueId}
        availableQueues={availableQueues as Array<{ id: string; nome: string; realStatus: string }>}
        onAdd={handleAddScheduledQueue}
      />

      {/* Dialog: Confirmar desativação */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar catálogo</DialogTitle>
            <DialogDescription>
              O link público do catálogo deixará de funcionar para todos os participantes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeactivate}>
              Desativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar regeneração de token */}
      <Dialog
        open={showRegenerateDialog}
        onOpenChange={(open) => {
          setShowRegenerateDialog(open);
          if (!open) setNewToken('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerar token</DialogTitle>
            <DialogDescription>
              Isso invalidará o link atual do catálogo. Todos os participantes com o link antigo
              perderão o acesso imediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium text-foreground">Novo token (opcional)</label>
            <Input
              placeholder="meu-evento-2026 (mínimo 8 caracteres)"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Deixe em branco para gerar automaticamente</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => regenerateMutation.mutate(newToken || undefined)}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? 'Regenerando...' : 'Regenerar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
